import { useState, useContext, useCallback } from "react";
import { AuthContext } from "src/utils/AuthContext.js";
import { useSnackbarContext } from "src/utils/SnackbarContext.js";
import {
  redirectSignedOutUser,
  handleSubscribeExperience,
} from "src/utils/Utils.js";
import { requestChannelDecision } from "src/utils/googleConnectIntercept.js";
import { useNavigate } from "react-router-dom";
import {
  getPageRoute,
  getApiRoute,
  useGetUserCalendars,
  useUnsubscribeCalendar,
  hasAccess,
  getUpgradeFlow,
  Features,
} from "src/services";
import { useAddToGroup } from "src/services";
import { Box, Icon } from "@mui/material";
import axios from "axios";

/** Calendar ids of a target: one calendar or an array of them. */
const toCalendarIds = (target) =>
  Array.isArray(target) ? target.map((c) => c._id ?? c.calendarId) : [target._id];

/** The single calendar that represents a target in upgrade-flow context. */
const firstCalendar = (target) => (Array.isArray(target) ? target[0] : target);

/** Sentinel busyGroupId while a brand-new group is being created (no id yet). */
const CREATING_GROUP_ID = "__creating__";

/**
 *
 * @param {boolean} moveCalendarToGroup - If true, the user is unsubscribing and adding the calendar to a group.
 * @param {boolean} closeOnAdd - If true, the add-to-group dialog closes as soon as a group is picked.
 * @param {boolean} fetchUserCalendars - When false, the user-calendars query waits for an explicit refetch.
 * @param {Function} onSettled - Reports each group op's outcome: (movedFully, target).
 */
export const useGroupDialogs = ({
  moveCalendarToGroup = false,
  closeOnAdd = false,
  fetchUserCalendars = true,
  setCreatedGroups,
  onSubscribeAction = () => {},
  onSettled = () => {},
  setSubscribed = () => {},
}) => {
  const { user, refetchAuth } = useContext(AuthContext);
  const {
    setSnackbarOpen,
    setSnackbarMessage,
    setSnackbarSeverity,
    showSnackBar,
  } = useSnackbarContext();
  const [openAddToGroup, setOpenAddToGroup] = useState(false);
  const [openNewGroup, setOpenNewGroup] = useState(false);
  const [busyGroupId, setBusyGroupId] = useState(null);
  const { mutateAsync: addToGroup } = useAddToGroup();
  const navigate = useNavigate();
  const [handleMessage, setHandleMessage] = useState("");
  const [targetCalendar, setTargetCalendar] = useState(false);
  const { refetch: refetchUserCalendars } = useGetUserCalendars(
    { user },
    { enabled: fetchUserCalendars },
  );
  const { mutateAsync: unsubscribeCalendar } = useUnsubscribeCalendar();

  const handleGroupDialog = useCallback(
    async (targetCalendar, extra = { user }) => {
      const { user } = extra;
      if (redirectSignedOutUser(user, navigate)) return;

      // Check if user has access to add calendars to groups
      if (!hasAccess(user, Features.ADD_TO_GROUP.key)) {
        const upgradeFlow = getUpgradeFlow(Features.ADD_TO_GROUP.key, {
          calendar: firstCalendar(targetCalendar),
          currentPath: window.location.pathname + window.location.search,
        });
        return navigate(upgradeFlow.route, { state: upgradeFlow.state });
      }

      try {
        // Fetch the latest calendars / groups user has added / created
        const data = await refetchUserCalendars();
        setTargetCalendar(targetCalendar);
        setCreatedGroups(data?.data?.createdGroups ?? []);
        if (data?.data?.createdGroups?.length > 0) {
          setOpenAddToGroup(true);
        } else {
          setOpenNewGroup(true);
        }
      } catch (error) {
        console.error(error);
        showSnackBar(
          setSnackbarOpen,
          setSnackbarMessage,
          setSnackbarSeverity,
          error.message,
          "error"
        );
      }
    },
    [
      navigate,
      refetchUserCalendars,
      setCreatedGroups,
      setSnackbarMessage,
      setSnackbarOpen,
      setSnackbarSeverity,
      showSnackBar,
      user,
    ]
  );

  /**
   * Drops the standalone copies of the target after they were grouped.
   *
   * @returns {Promise<boolean>} False when any unsubscribe failed; the caller owns the message.
   */
  const handleUnsubscribeFromCalendar = useCallback(async () => {
    try {
      // Sequential (not Promise.all) so a bundle of moves doesn't fan out
      // concurrent unsubscribe calls at the API.
      for (const calendarId of toCalendarIds(targetCalendar)) {
        await unsubscribeCalendar({ calendarId });
      }
      return true;
    } catch (error) {
      return false;
    }
  }, [targetCalendar, unsubscribeCalendar]);

  /**
   * Warns that a group op succeeded but the loose copies are still in the library.
   *
   * @param {string} groupName - The group the calendars were added to.
   */
  const showPartialMoveWarning = useCallback(
    (groupName) =>
      showSnackBar(
        setSnackbarOpen,
        setSnackbarMessage,
        setSnackbarSeverity,
        `Added to ${groupName}, but we couldn't remove the separate copies. You can remove them from your Library.`,
        "warning"
      ),
    [showSnackBar, setSnackbarOpen, setSnackbarMessage, setSnackbarSeverity]
  );

  const handleAddToGroup = useCallback(
    async (group) => {
      setBusyGroupId(group._id);
      // Dismiss the picker right away so the caller can show progress inline.
      if (closeOnAdd) setOpenAddToGroup(false);
      try {
        // Group FIRST, then drop the loose copies: if the unsubscribe half
        // fails the calendar is merely duplicated (loose + grouped), whereas
        // unsubscribing first could lose it entirely when the add failed.
        await addToGroup({
          groupId: group._id,
          calendarIds: toCalendarIds(targetCalendar),
        });
        const movedLoose = moveCalendarToGroup ? await handleUnsubscribeFromCalendar() : true;
        onSubscribeAction?.(true, "group", targetCalendar, group._id);
        const defaultFn = () =>
          setTimeout(() => {
            showSnackBar(
              setSnackbarOpen,
              setSnackbarMessage,
              setSnackbarSeverity,
              <Box
                display="inline"
                onClick={() => navigate(`/${group.handle}/${group._id}`)}
                sx={{ cursor: "pointer" }}
              >
                Added to{" "}
                <Icon
                  baseClassName="material-symbols-outlined"
                  sx={{
                    fontWeight: "300",
                    fontSize: "1rem",
                    ml: 0.75,
                    mr: 0.5,
                    verticalAlign: "text-bottom",
                  }}
                >
                  folder
                </Icon>{" "}
                {group.name}{" "}
                <Icon
                  baseClassName="material-symbols-outlined"
                  sx={{
                    fontWeight: "300",
                    fontSize: "1rem",
                    verticalAlign: "text-bottom",
                  }}
                >
                  north_east
                </Icon>
              </Box>,
              "success"
            );
          }, 1000);
        // Deferred-handoff consumers (closeOnAdd) own the calendar-app step
        // themselves — show the snackbar only, never the channel intercept.
        if (!movedLoose) {
          showPartialMoveWarning(group.name);
        } else if (!closeOnAdd && window.location.pathname !== getPageRoute("library", "LIBRARY")) {
          handleSubscribeExperience({
            user,
            navigate,
            calendar: group,
            defaultFn,
            onHasValidGoogleConnection: defaultFn,
            onChannelIntercept: requestChannelDecision,
          });
        } else {
          defaultFn();
        }
        // closeOnAdd already dismissed the picker; closing again here would shut
        // a picker another concurrent op has since opened.
        if (!closeOnAdd) setOpenAddToGroup(false);
        setSubscribed(true);
        refetchUserCalendars();
        refetchAuth();
        onSettled?.(movedLoose, targetCalendar);
      } catch (error) {
        console.log(error);
        showSnackBar(
          setSnackbarOpen,
          setSnackbarMessage,
          setSnackbarSeverity,
          error.message,
          "error"
        );
        onSettled?.(false, targetCalendar);
      } finally {
        setBusyGroupId((current) => (current === group._id ? null : current));
      }
    },
    [
      moveCalendarToGroup,
      closeOnAdd,
      addToGroup,
      targetCalendar,
      onSubscribeAction,
      onSettled,
      setSubscribed,
      refetchUserCalendars,
      refetchAuth,
      handleUnsubscribeFromCalendar,
      showPartialMoveWarning,
      showSnackBar,
      setSnackbarOpen,
      setSnackbarMessage,
      setSnackbarSeverity,
      navigate,
      user,
    ]
  );

  const handleCreateGroup = useCallback(
    async (groupName, groupDescription, handle) => {
      setBusyGroupId(CREATING_GROUP_ID);
      try {
        const response = await axios.post(
          getApiRoute("user", "CREATE_GROUP"),
          {
            name: groupName,
            description: groupDescription,
            tags: [],
            handle: handle ? handle : user?.handle,
            calendars: toCalendarIds(targetCalendar),
          },
          { withCredentials: true }
        );

        console.log("Group created successfully");
        // Loose copies drop only after the group exists (see handleAddToGroup).
        const movedLoose = moveCalendarToGroup ? await handleUnsubscribeFromCalendar() : true;
        onSubscribeAction?.(true, "group", targetCalendar, response.data.groupId);
        if (!movedLoose) showPartialMoveWarning(groupName);
        else setTimeout(() => {
          showSnackBar(
            setSnackbarOpen,
            setSnackbarMessage,
            setSnackbarSeverity,
            <Box
              display="inline"
              onClick={() =>
                navigate(
                  `/${handle ? handle : user?.handle}/${response.data.groupId}`
                )
              }
              sx={{ cursor: "pointer" }}
            >
              <Icon
                baseClassName="material-symbols-outlined"
                sx={{
                  fontWeight: "300",
                  fontSize: "1rem",
                  ml: 0.75,
                  mr: 0.5,
                  verticalAlign: "text-bottom",
                }}
              >
                folder
              </Icon>{" "}
              {groupName} successfully created{" "}
              <Icon
                baseClassName="material-symbols-outlined"
                sx={{
                  fontWeight: "300",
                  fontSize: "1rem",
                  verticalAlign: "text-bottom",
                }}
              >
                north_east
              </Icon>
            </Box>,
            "success"
          );
        }, 1000);
        setOpenNewGroup(false);
        setSubscribed(true);
        refetchUserCalendars();
        refetchAuth();
        onSettled?.(movedLoose, targetCalendar);
      } catch (error) {
        onSettled?.(false, targetCalendar);
        if (error.response && error.response.status === 400) {
          setHandleMessage("Handle already taken. Pick another one.");
        }
        if (error.response && error.response.status === 403) {
          // Handle upgrade flow via feature gating service
          const upgradeFlow = getUpgradeFlow(Features.CREATE_GROUP.key, {
            calendar: firstCalendar(targetCalendar),
            currentPath: window.location.pathname + window.location.search,
          });
          return navigate(upgradeFlow.route, { state: upgradeFlow.state });
        } else {
          showSnackBar(
            setSnackbarOpen,
            setSnackbarMessage,
            setSnackbarSeverity,
            error.message,
            "error"
          );
        }
      } finally {
        setBusyGroupId((current) => (current === CREATING_GROUP_ID ? null : current));
      }
    },
    [
      moveCalendarToGroup,
      user,
      targetCalendar,
      onSubscribeAction,
      onSettled,
      setSubscribed,
      refetchUserCalendars,
      refetchAuth,
      handleUnsubscribeFromCalendar,
      showPartialMoveWarning,
      showSnackBar,
      setSnackbarOpen,
      setSnackbarMessage,
      setSnackbarSeverity,
      navigate,
    ]
  );

  return {
    openAddToGroup,
    openNewGroup,
    setOpenAddToGroup,
    setOpenNewGroup,
    handleGroupDialog,
    handleAddToGroup,
    handleCreateGroup,
    handleMessage,
    targetCalendar,
    busyGroupId,
  };
};
